'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FaCalendarAlt, 
  FaCheck, 
  FaChevronLeft, 
  FaChevronRight, 
  FaLock, 
  FaLockOpen, 
  FaPlus, 
  FaSave, 
  FaTrash,
  FaExclamationTriangle,
  FaSync
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { BlockedPeriod } from '../../../../services/calendarService';
import { useCalendar } from '../../../../context/CalendarContext';
import { toast } from 'react-hot-toast';
import { formatDate, withTimeout } from '../../../../utils/calendarUtils';
import { forceClearCalendarCache, broadcastCalendarUpdate } from '../../../../utils/calendarSyncUtils';

// Props
interface AvailabilityManagerProps {
  initialDate?: Date;
  onDataChange?: () => void;
}

// Types
type DayAvailability = {
  date: string;
  available: boolean;
};

export default function AvailabilityManager({ initialDate, onDataChange }: AvailabilityManagerProps) {
  // Get calendar context
  const { 
    settings, 
    isLoading: contextIsLoading,
    errors,
    fetchCalendarSettings,
    updateCalendarSettings,
    addBlockedPeriod,
    deleteBlockedPeriod,
    clearErrors
  } = useCalendar();
  
  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || new Date());
  const [newBlockedPeriod, setNewBlockedPeriod] = useState<{
    startDate: string;
    endDate: string;
    reason: string;
  }>({
    startDate: formatDate(new Date()),
    endDate: formatDate(new Date()),
    reason: ''
  });
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  
  // Refs
  const isMountedRef = useRef<boolean>(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Load settings when component mounts
  useEffect(() => {
    const loadSettings = async () => {
      if (!settings || Object.keys(settings).length === 0) {
        try {
          setIsLoading(true);
          
          // Set safety timeout
          timeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && isLoading) {
              console.log('AvailabilityManager: Safety timeout triggered');
              setIsLoading(false);
              toast.error('Settings loading timed out. Please try refreshing.');
            }
          }, 5000);
          
          // Fetch settings with timeout
          await withTimeout(
            fetchCalendarSettings(),
            8000,
            'Calendar settings fetch timed out'
          );
          
          // Call onDataChange callback if provided
          if (onDataChange) {
            onDataChange();
          }
        } catch (error) {
          console.error('Error loading calendar settings:', error);
          toast.error('Failed to load calendar settings');
        } finally {
          if (isMountedRef.current) {
            setIsLoading(false);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
          }
        }
      }
    };
    
    loadSettings();
  }, [settings, fetchCalendarSettings, onDataChange, isLoading]);
  
  // Navigate to previous month
  const goToPreviousMonth = useCallback(() => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);
  
  // Navigate to next month
  const goToNextMonth = useCallback(() => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);
  
  // Format month and year
  const formattedMonthYear = useCallback(() => {
    return currentDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }, [currentDate]);
  
  // Handle form change for new blocked period
  const handleFormChange = useCallback((field: string, value: string) => {
    setNewBlockedPeriod(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  
  // Add new blocked period
  const handleAddBlockedPeriod = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Validate dates
      const startDate = new Date(newBlockedPeriod.startDate);
      const endDate = new Date(newBlockedPeriod.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        toast.error('Please enter valid dates');
        setIsLoading(false);
        return;
      }
      
      if (endDate < startDate) {
        toast.error('End date cannot be before start date');
        setIsLoading(false);
        return;
      }
      
      // Add blocked period with timeout
      const newPeriod = await withTimeout(
        addBlockedPeriod({
          startDate: newBlockedPeriod.startDate,
          endDate: newBlockedPeriod.endDate,
          reason: newBlockedPeriod.reason || 'Blocked by admin'
        }),
        5000,
        'Add blocked period timed out'
      );
      
      // Force clear calendar cache to ensure landing page gets fresh data
      forceClearCalendarCache();
      
      // Broadcast calendar update to other tabs/windows
      broadcastCalendarUpdate('blocked-period-added', {
        startDate: newBlockedPeriod.startDate,
        endDate: newBlockedPeriod.endDate
      });
      
      // Reset form
      setNewBlockedPeriod({
        startDate: formatDate(new Date()),
        endDate: formatDate(new Date()),
        reason: ''
      });
      
      // Hide form
      setShowAddForm(false);
      
      // Call onDataChange callback if provided
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Blocked period added successfully');
      console.log('Calendar sync: Blocked period added and synchronized');
    } catch (error) {
      console.error('Error adding blocked period:', error);
      toast.error('Failed to add blocked period');
    } finally {
      setIsLoading(false);
    }
  }, [newBlockedPeriod, addBlockedPeriod, onDataChange]);
  
  // Delete blocked period
  const handleDeleteBlockedPeriod = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      
      // Find the period to be deleted to get its date range for broadcasting
      let periodToDelete: BlockedPeriod | undefined;
      if (settings) {
        periodToDelete = settings.blockedPeriods.find(period => period.id === id);
      }
      
      // Delete blocked period with timeout
      await withTimeout(
        deleteBlockedPeriod(id),
        5000,
        'Delete blocked period timed out'
      );
      
      // Force clear calendar cache to ensure landing page gets fresh data
      forceClearCalendarCache();
      
      // Broadcast calendar update to other tabs/windows
      if (periodToDelete) {
        broadcastCalendarUpdate('blocked-period-removed', {
          id,
          startDate: periodToDelete.startDate,
          endDate: periodToDelete.endDate
        });
      } else {
        broadcastCalendarUpdate('blocked-period-removed', { id });
      }
      
      // Call onDataChange callback if provided
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Blocked period deleted successfully');
      console.log('Calendar sync: Blocked period removed and synchronized');
    } catch (error) {
      console.error('Error deleting blocked period:', error);
      toast.error('Failed to delete blocked period');
    } finally {
      setIsLoading(false);
    }
  }, [deleteBlockedPeriod, onDataChange, settings]);
  
  // Toggle add form
  const toggleAddForm = useCallback(() => {
    setShowAddForm(prev => !prev);
  }, []);
  
  // Render blocked periods
  const renderBlockedPeriods = useCallback(() => {
    if (!settings || !settings.blockedPeriods) {
      return (
        <div className="text-gray-500 text-center py-4">
          No blocked periods defined
        </div>
      );
    }
    
    // Filter blocked periods for current month/year
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    const filteredPeriods = settings.blockedPeriods.filter(period => {
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      
      // Check if period overlaps with current month
      return (
        (startDate.getFullYear() === currentYear && startDate.getMonth() === currentMonth) ||
        (endDate.getFullYear() === currentYear && endDate.getMonth() === currentMonth) ||
        (startDate < new Date(currentYear, currentMonth, 1) && 
         endDate > new Date(currentYear, currentMonth + 1, 0))
      );
    });
    
    if (filteredPeriods.length === 0) {
      return (
        <div className="text-gray-500 text-center py-4">
          No blocked periods for this month
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {filteredPeriods.map(period => (
          <motion.div
            key={period.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-3 rounded-md border border-gray-200 shadow-sm"
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center text-gray-800">
                  <FaLock className="mr-2 text-red-500" size={12} />
                  <span className="font-medium">
                    {new Date(period.startDate).toLocaleDateString()} - {new Date(period.endDate).toLocaleDateString()}
                  </span>
                </div>
                {period.reason && (
                  <p className="text-sm text-gray-600 mt-1">{period.reason}</p>
                )}
              </div>
              <button
                onClick={() => handleDeleteBlockedPeriod(period.id)}
                className="text-red-500 hover:text-red-700"
                disabled={isLoading}
              >
                <FaTrash size={14} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    );
  }, [settings, currentDate, isLoading, handleDeleteBlockedPeriod]);
  
  return (
    <div className="h-full relative">
      {/* Loading overlay */}
      {(contextIsLoading || isLoading) && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-2 text-sm text-gray-600">Loading availability data...</p>
          </div>
        </div>
      )}
      
      {/* Error display */}
      {(errors.settings || errors.operations) && (
        <div className="bg-red-50 p-4 rounded-md mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading availability data</h3>
              <p className="text-sm text-red-700 mt-1">
                {errors.settings instanceof Error ? errors.settings.message : 
                 errors.operations instanceof Error ? errors.operations.message : 
                 errors.settings || errors.operations || 'An unknown error occurred'}
              </p>
              <button
                onClick={clearErrors}
                className="mt-2 text-sm text-red-700 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="space-y-6">
        {/* Month navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            disabled={isLoading}
          >
            <FaChevronLeft className="text-gray-600" />
          </button>
          
          <h2 className="text-xl font-medium text-gray-900">
            {formattedMonthYear()}
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            disabled={isLoading}
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>
        
        {/* Blocked periods section */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Blocked Periods
            </h3>
            
            <button
              onClick={toggleAddForm}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={isLoading}
            >
              {showAddForm ? (
                <>Cancel</>
              ) : (
                <>
                  <FaPlus className="mr-1" size={12} />
                  Add Period
                </>
              )}
            </button>
          </div>
          
          {/* Add form */}
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-white p-4 rounded-md border border-gray-200 shadow-sm mb-4"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={newBlockedPeriod.startDate}
                      onChange={(e) => handleFormChange('startDate', e.target.value)}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      value={newBlockedPeriod.endDate}
                      onChange={(e) => handleFormChange('endDate', e.target.value)}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (Optional)
                  </label>
                  <input
                    type="text"
                    id="reason"
                    value={newBlockedPeriod.reason}
                    onChange={(e) => handleFormChange('reason', e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="e.g., Maintenance, Personal use, etc."
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddBlockedPeriod}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="mr-2" size={14} />
                        Add Period
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Blocked periods list */}
          {renderBlockedPeriods()}
        </div>
        
        {/* Base price section */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Base Price Settings
          </h3>
          
          {settings && (
            <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm">
              <div className="text-lg font-medium text-gray-900">
                ${settings.basePrice} <span className="text-sm text-gray-500">per night</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Base price before weekday and seasonal adjustments
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
